import {
  revalidateCartAgainstCatalog,
  SCHEDULE_WINDOW_REASONS,
  type CatalogSnapshot,
  type LineRevalidation,
  type LineUnavailabilityReason,
} from "@/lib/revalidate-checkout-catalog"
import { sumCartItems } from "@/lib/pricing"
import type { Cart } from "@/types"

const OPTION_LEVEL_REASONS: ReadonlySet<LineUnavailabilityReason> = new Set([
  "option_missing",
  "option_paused",
  "option_group_missing",
  "option_group_unsatisfied",
])

export interface PruneOptions {
  /**
   * Whether a line that is merely outside its hours right now is removed too.
   * False while the store sells future windows: the checkout decides those.
   */
  pruneOutOfSchedule: boolean
}

export interface PruneResult {
  /** The bag with the invalid lines removed and the kept lines repriced. */
  cart: Cart
  /** What was removed and why, in bag order. */
  removed: LineRevalidation[]
  /** True when a kept line's total moved against the catalog. */
  pricesMoved: boolean
}

/**
 * The reorder rule applied to a bag the customer already has: every line the
 * live catalog no longer accepts (gone, paused, paused option, unfilled
 * mandatory group, and optionally out of hours) leaves the bag, the rest stays
 * at today's prices. Pure; the caller persists and tells the customer.
 */
export function pruneCartAgainstCatalog(
  cart: Cart,
  catalog: CatalogSnapshot,
  options: PruneOptions,
): PruneResult {
  const revalidation = revalidateCartAgainstCatalog(cart, catalog)
  const removedIds = new Set<string>()
  const removed: LineRevalidation[] = []

  for (const line of revalidation.lines) {
    if (line.reason === null) continue
    if (!options.pruneOutOfSchedule && SCHEDULE_WINDOW_REASONS.has(line.reason)) continue
    removedIds.add(line.lineId)
    removed.push(line)
  }

  const keptItems = revalidation.refreshedCart.items.filter((item) => !removedIds.has(item.id))
  const pricesMoved = revalidation.lines.some(
    (line) => !removedIds.has(line.lineId) && cents(line.previousTotal) !== cents(line.refreshedTotal),
  )

  return {
    cart: { ...cart, items: keptItems, totalPrice: sumCartItems(keptItems) },
    removed,
    pricesMoved,
  }
}

function cents(value: number): number {
  return Math.round((Number(value) || 0) * 100)
}

/** Label shown to the customer for one removed line. */
export function formatRemovedLineLabel(line: LineRevalidation): string {
  const item = line.name.trim()
  if (!line.reason || !OPTION_LEVEL_REASONS.has(line.reason)) {
    return item
  }
  if (line.optionName?.trim()) {
    return `${item} (opção “${line.optionName.trim()}”)`
  }
  if (line.optionGroupName?.trim()) {
    return `${item} (grupo “${line.optionGroupName.trim()}”)`
  }
  return item
}

/** "“A”, “B” e mais 2 itens": a readable list that names what left the bag. */
export function formatRemovedItemsMessage(lines: Array<string | LineRevalidation>): string {
  const labels = lines.map((line) =>
    typeof line === "string" ? line.trim() : formatRemovedLineLabel(line),
  )
  const MAX_NAMES = 3
  const shown = labels.slice(0, MAX_NAMES).map((name) => `“${name}”`)
  const hiddenCount = labels.length - shown.length
  if (hiddenCount > 0) shown.push(`mais ${hiddenCount} ${hiddenCount === 1 ? "item" : "itens"}`)
  const list = shown.length === 1 ? shown[0] : `${shown.slice(0, -1).join(", ")} e ${shown[shown.length - 1]}`
  return labels.length === 1
    ? `${list} saiu da sacola porque o cardápio mudou. Se quiser, adicione o item novamente pelo menu.`
    : `${list} saíram da sacola porque o cardápio mudou. Se quiser, adicione os itens novamente pelo menu.`
}
